import { Tool } from './base'
import {
  InlineGroups,
  makeRenderer,
  normalizeList,
  PipeAndGroups,
  RenderContext,
  RenderOut,
  scalarToString,
} from './render/render-kit'
import { NormalizedDataSource } from './render/rtd/datasource.types'
import { joinDirect, wrapRendererWithDatasource } from './render/rtd/datasource.utils'
import { s1LegacyMap } from './render/rtd/tool-datasource-mappings/sentinelone.datasource'
import { AstPredicate, ValueOrList } from './render/types'

export { s1LegacyMap }

export class SentinelOneTool extends Tool {
  public readonly toolName = 'SentinelOne'
  public readonly toolKind = 'edr' as const

  public generateSearchPermalink(
    rootURL: string,
    renderedContent: string,
  ): string {
    const encodedRenderedContent = encodeURIComponent(renderedContent)
    return `${rootURL}/dv/hunting?queryString=${encodedRenderedContent}&timeFrame=Last24Hours&isRunQuery=false`
  }

  // “
  public pivotEscape(key: string, value: string): string {
    return value.replace(/"/g, '\\"')
  }

  /** Notes and Implementation
   * Grouping & Layout
   * - InlineGroups: no root parentheses; nested groups parenthesized.
   *
   * Field mapping
   * - toolTuningFields["sentinelone"]; no Eta wrapping.
   *
   * Quoting & Lists
   * - **Smart quoting**: numbers are bare; strings are **double-quoted** with `"` escaping (backslashes left as-is).
   * - For lists, we build type-aware items: `(<item1>, <item2>, ...)`.
   *
   * Operators (verbatim forms)
   * - eq / neq:
   *    • single: `F = V` / `F != V`
   *    • multi:  `F in (L)` / `F Not In (L)`
   * - contains / doesnotcontain:
   *    • single: `F Contains Anycase V` / `NOT F Contains Anycase V`
   *    • multi:  `F In Contains Anycase (L)` / `NOT F In Contains Anycase (L)`
   * - startswith / endswith:
   *    • single: `F StartsWith Anycase V` / `F EndsWith Anycase V`
   *    • multi:  OR chain of single clauses in parentheses
   * - lt/lte/gt/gte: `<`, `<=`, `>`, `>=` (values smart-quoted)
   *
   * Null / Empty (corrected)
   * - isnull     → `NOT F EXISTS`
   * - isnotnull  → `F EXISTS`
   * - isempty    → `F Is Empty`
   * - isnotempty → `F Is Not Empty`
   *
   * Safety
   * - Identifiers sanitized by mapField; centralized smart quoting done locally.
   */

  public renderTuning = makeRenderer({
    tool: this.toolName,
    groupStrategy: InlineGroups,
    renderContext: {
      wrapField: (f) => f,
      andGlue: ' AND ',
      orGlue: ' OR ',
      // We still provide a basic quote() for string cases, but smart quoting is
      // applied inside the formatter using raw types.
      quote: (s: string) => `"${String(s).replace(/"/g, '\\"')}"`,
    },

    formatPredicate: (p: AstPredicate, ctx: RenderContext): string => {
      const F = ctx.wrapField(ctx.mapField(p.field))

      // Keep raw values to decide number vs string at render time
      const raw: ValueOrList | undefined = p.value
      const rawList =
        raw == null ? [undefined] : Array.isArray(raw) ? raw : [raw]

      // Stringified values for uniform shaping when needed
      const vals = normalizeList(p.value).map(scalarToString)
      const isMulti = vals.length > 1

      // Smart quote: numbers → bare; strings → double-quoted
      const Q = (s: string, rv: any) =>
        typeof rv === 'number' ? String(rv) : ctx.quote(s)

      const LIST = () => {
        const items = vals.map((s, i) => Q(s, rawList[i]))
        return `(${items.join(', ')})`
      }

      // Null/empty (corrected)
      switch (p.op) {
        case 'isnull':
          return `NOT ${F} EXISTS`
        case 'isnotnull':
          return `${F} EXISTS`
        case 'isempty':
          return `${F} Is Empty`
        case 'isnotempty':
          return `${F} Is Not Empty`
      }

      // Helpers to expand multi-value OR chains for starts/ends
      const OR = (parts: string[]) => `(${parts.join(ctx.orGlue)})`

      switch (p.op) {
        // Equality / inequality
        case 'eq':
          return isMulti
            ? `${F} In AnyCase ${LIST()}`
            : `${F} = ${Q(vals[0], rawList[0])}`
        case 'neq':
          return isMulti
            ? `${F} Not In ${LIST()}`
            : `${F} != ${Q(vals[0], rawList[0])}`

        // Contains / doesnotcontain
        case 'contains':
          return isMulti
            ? `${F} In Contains AnyCase ${LIST()}`
            : `${F} Contains AnyCase ${Q(vals[0], rawList[0])}`
        case 'doesnotcontain':
          return isMulti
            ? `NOT ${F} In Contains AnyCase ${LIST()}`
            : `NOT ${F} Contains AnyCase ${Q(vals[0], rawList[0])}`

        // StartsWith / EndsWith
        case 'startswith':
          return isMulti
            ? OR(
                vals.map(
                  (s, i) => `${F} StartsWith AnyCase ${Q(s, rawList[i])}`,
                ),
              )
            : `${F} StartsWith AnyCase ${Q(vals[0], rawList[0])}`
        case 'endswith':
          return isMulti
            ? OR(
                vals.map((s, i) => `${F} EndsWith AnyCase ${Q(s, rawList[i])}`),
              )
            : `${F} EndsWith AnyCase ${Q(vals[0], rawList[0])}`

        // Comparisons (smart quoting)
        case 'lt':
          return `${F} < ${Q(vals[0], rawList[0])}`
        case 'lte':
          return `${F} <= ${Q(vals[0], rawList[0])}`
        case 'gt':
          return `${F} > ${Q(vals[0], rawList[0])}`
        case 'gte':
          return `${F} >= ${Q(vals[0], rawList[0])}`

        default:
          // Fallback equals
          return isMulti
            ? `${F} in ${LIST()}`
            : `${F} = ${Q(vals[0], rawList[0])}`
      }
    },
    // Fix to always prefix the snippet with AND
    finalize: (out: RenderOut, renderContext: RenderContext): string => {
      const base =
        out.kind === 'text' ? out.text : out.lines.join(renderContext.andGlue)

      if (!base) return ''
      return `${renderContext.andGlue}(${base})`
    },
  })

  public readonly tuningText =
    'Append tuning logic to the end of the hunt logic. In some cases the hunt logic will need to be encapsulated in parenthesis, when tuning logic does not work as desired.'

  public override readonly datasourceMap = s1LegacyMap

  /**
   * Emit the SentinelOne Legacy prelude — the complete ObjectType/EventType clause
   * stored in events[0] (e.g. `ObjectType = "process" AND EventType = "Process Creation"`).
   */
  public emitS1LegacyDatasource(ds: NormalizedDataSource | undefined): string {
    return ds?.events?.[0] ?? ''
  }

  /** Emit prelude + finalize()-prefixed tuning body joined directly (no extra separator). */
  public readonly renderSentinelOne = wrapRendererWithDatasource(
    this.renderTuning,
    this.toolName,
    (ds) => this.emitS1LegacyDatasource(ds),
    joinDirect,
    this.tuningText,
  )
}
